"use client"

import { CircleHelp } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
/**
 * A single step in a "How To Use" walkthrough.
 *
 * @property title - Short heading for the step (callers own any numbering).
 * @property body  - Explanatory text describing what the user does in this step.
 */
export interface HowToStep {
  title: string
  body: string
}

/**
 * The full content of a "How To Use" guide for a single tool/agent.
 *
 * @property title    - Dialog heading (e.g. "How To Use: <Tool> Agent").
 * @property intro    - Optional lead-in paragraph shown above the steps.
 * @property steps    - Ordered walkthrough steps.
 * @property footnote - Optional small print shown beneath the steps.
 */
export interface HowToUseContent {
  title: string
  intro?: string
  steps: readonly HowToStep[]
  footnote?: string
}

/**
 * Props for {@link HowToUseDialog}.
 *
 * @property content          - The guide content to render (title, intro, steps, footnote).
 * @property triggerLabel     - Text shown in the hover tooltip and used as the button's
 *   accessible label. Defaults to "How to use". The button itself renders only a `?` icon.
 * @property triggerClassName - Optional class names applied to the trigger button so callers
 *   can adjust spacing/sizing for the surrounding layout.
 */
interface HowToUseDialogProps {
  content: HowToUseContent
  triggerLabel?: string
  triggerClassName?: string
}

/**
 * A generic, agent-agnostic "More Info" / "How To Use" affordance.
 *
 * Renders an icon-only help button (a `?` glyph) that opens a dialog presenting
 * a titled, step-by-step walkthrough supplied via {@link HowToUseContent}. On
 * hover/focus the button surfaces a tooltip with {@link triggerLabel} text.
 * Any tool or agent can reuse this by passing its own content — see
 * `howToUseContent.ts` for the per-agent registry used by the chat UI.
 *
 * The dialog is uncontrolled — the shadcn/Radix Dialog manages its own open
 * state via the DialogTrigger, so no external state is required.
 *
 * @param content          - The guide content (title, intro, steps, footnote).
 * @param triggerLabel     - Tooltip text and accessible label for the button.
 * @param triggerClassName - Optional extra classes for the trigger button.
 * @returns The rendered trigger button and its associated dialog.
 */
export function HowToUseDialog({
  content,
  triggerLabel = "How to use",
  triggerClassName,
}: HowToUseDialogProps): React.JSX.Element {
  return (
    <Dialog>
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="icon-sm"
              className={cn("rounded-full", triggerClassName)}
              aria-label={triggerLabel}
            >
              <CircleHelp />
            </Button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">{triggerLabel}</TooltipContent>
      </Tooltip>

      <DialogContent
        className="sm:max-w-md md:max-w-2xl p-0 overflow-hidden gap-0"
        aria-describedby={undefined}
      >
        {/* Styled header pill, mirroring the existing ChatInfoDialog pattern. */}
        <div className="bg-muted px-6 py-4 flex items-center gap-3">
          <div className="flex items-center justify-center rounded-full bg-primary p-2">
            <CircleHelp className="h-4 w-4 text-primary-foreground" />
          </div>
          <DialogHeader className="p-0 gap-0">
            <DialogTitle className="text-foreground text-base leading-tight">
              {content.title}
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {content.intro && (
            <p className="text-sm text-muted-foreground">{content.intro}</p>
          )}

          <ol className="space-y-4">
            {content.steps.map((step) => (
              <li key={step.title} className="space-y-1">
                <h3 className="text-sm font-medium text-foreground">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>
              </li>
            ))}
          </ol>

          {content.footnote && (
            <p className="text-xs text-muted-foreground border-t pt-4">{content.footnote}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
